import React, { useEffect, useState, useMemo } from 'react';
import { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { ItemCardHeader, MarkdownContent, UserIcon } from '@backstage/core-components';
import {
  catalogApiRef,
  EntityRefLinks,
  getEntityRelations,
  FavoriteEntity,
} from '@backstage/plugin-catalog-react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  Button,
  makeStyles,
  Grid,
  Divider,
  Box,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';
import { BackstageTheme } from '@backstage/theme';

const ALLOWED_GROUPS_ANNOTATION = 'backstage.io/allowed-groups';

const useStyles = makeStyles<BackstageTheme>(theme => {
  const pageTheme = theme.getPageTheme({ themeId: 'home' });
  return {
    cardMedia: {
      position: 'relative',
    },
    cardHeader: {
      backgroundImage: pageTheme.backgroundImage,
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      color: pageTheme.fontColor,
    },
    headerActions: {
      position: 'absolute',
      top: 8,
      right: 8,
      display: 'flex',
      gap: 4,
    },
    headerActionButton: {
      color: pageTheme.fontColor,
      padding: 4,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
  box: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 10,
    '-webkit-box-orient': 'vertical',
  },
  markdown: {
    '& :first-child': {
      margin: 0,
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    flex: 1,
    alignItems: 'center',
  },
  ownedBy: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    color: theme.palette.link,
  },
  actionContainer: {
    padding: '16px',
    flex: 1,
    alignItems: 'flex-end',
  },
};
});

interface CustomTemplateCardProps {
  template: TemplateEntityV1beta3;
}

export const CustomTemplateCard = ({ template }: CustomTemplateCardProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserGroups = async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const groupsFromIdentity = (identity?.ownershipEntityRefs || [])
          .filter(ref => ref.startsWith('group:'))
          .map(ref => ref.toLowerCase());

        const userEntityRef = identity?.userEntityRef;
        if (!userEntityRef) {
          setUserGroups(groupsFromIdentity);
          return;
        }

        try {
          const userEntity = await catalogApi.getEntityByRef(userEntityRef);
          const groupsFromCatalog = (userEntity?.relations || [])
            .filter(relation => relation.type === 'memberOf')
            .map(relation => relation.targetRef.toLowerCase());

          setUserGroups(
            Array.from(new Set([...groupsFromIdentity, ...groupsFromCatalog])),
          );
        } catch {
          setUserGroups(groupsFromIdentity);
        }
      } catch {
        setUserGroups([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUserGroups();
  }, [identityApi, catalogApi]);

  const allowedGroupsAnnotation =
    template.metadata.annotations?.[ALLOWED_GROUPS_ANNOTATION];

  const isAllowed = useMemo(() => {
    if (!allowedGroupsAnnotation) return true;
    const allowedGroups = allowedGroupsAnnotation
      .split(',')
      .map(g => g.trim().toLowerCase());
    return allowedGroups.some(group => userGroups.includes(group));
  }, [allowedGroupsAnnotation, userGroups]);

  // Hide the card completely if user doesn't have access
  if (!loading && !isAllowed) {
    return null;
  }

  const handleChoose = () => {
    const namespace = template.metadata.namespace || 'default';
    navigate(`/create/templates/${namespace}/${template.metadata.name}`);
  };

  const { title, name, description, tags } = template.metadata;
  const type = template.spec?.type;

  // Get owned-by relations
  const ownedByRelations = getEntityRelations(template, 'ownedBy');

  const hasTags = tags && tags.length > 0;
  const displayDefaultDivider = !hasTags;

  const handleViewInCatalog = () => {
    const namespace = template.metadata.namespace || 'default';
    navigate(`/catalog/${namespace}/Template/${template.metadata.name}`);
  };

  return (
    <Card>
      <CardMedia className={classes.cardMedia}>
        <ItemCardHeader
          title={title || name}
          subtitle={type}
          classes={{ root: classes.cardHeader }}
        />
        <div className={classes.headerActions}>
          <Tooltip title="View in Catalog">
            <IconButton
              className={classes.headerActionButton}
              size="small"
              onClick={handleViewInCatalog}
            >
              <DescriptionIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <FavoriteEntity
            className={classes.headerActionButton}
            entity={template}
          />
        </div>
      </CardMedia>
      <CardContent>
        <Grid container spacing={2}>
          {/* Description */}
          <Grid item xs={12}>
            <Box className={classes.box}>
              <MarkdownContent
                content={description || 'No description'}
                className={classes.markdown}
              />
            </Box>
          </Grid>

          {/* Default divider if no tags */}
          {displayDefaultDivider && (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}

          {/* Tags section */}
          {hasTags && (
            <React.Fragment>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {tags.map((tag: string) => (
                    <Grid item key={tag}>
                      <Chip label={tag} size="small" style={{ margin: 0 }} />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </React.Fragment>
          )}
        </Grid>
      </CardContent>
      <CardActions className={classes.actionContainer}>
        <div className={classes.footer}>
          <div className={classes.ownedBy}>
            {ownedByRelations.length > 0 && (
              <React.Fragment>
                <UserIcon fontSize="small" />
                <EntityRefLinks
                  entityRefs={ownedByRelations}
                  defaultKind="Group"
                  hideIcons={true}
                  style={{ marginLeft: '8px' }}
                />
              </React.Fragment>
            )}
          </div>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={handleChoose}
          >
            Choose
          </Button>
        </div>
      </CardActions>
    </Card>
  );
};
